import type { Timestamp } from "firebase-admin/firestore";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import toFirestoreSchema from "../utils/toFirestoreSchema";

/** Max length for leave / cancellation free-text fields on the user document */
export const USER_LEAVE_TEXT_MAX = 500;

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Month + day birthday (no year). Month is 1-indexed, day is 1-indexed. */
export const BirthdaySchema = z
    .object({
        day: z.number().int().min(1).max(31),
        month: z.number().int().min(1).max(12),
    })
    .refine((val) => val.day <= DAYS_IN_MONTH[val.month - 1], {
        message: "Invalid day for the given month",
    });

export type Birthday = z.infer<typeof BirthdaySchema>;

const zTimestamp = z.custom<Timestamp>((val) => val && typeof val.toDate === "function", {
    message: "Expected Firestore Timestamp",
});

/** Closed leave period (client: Date) */
export const LeavePeriodSchema = z.object({
    endedAt: z.date(),
    reason: z.string().max(USER_LEAVE_TEXT_MAX).optional(),
    startedAt: z.date(),
});

/** Closed leave period (Firestore Timestamps) */
export const LeavePeriodRecordSchema = z.object({
    endedAt: zTimestamp,
    reason: z.string().max(USER_LEAVE_TEXT_MAX).optional(),
    startedAt: zTimestamp,
});

export type LeavePeriod = z.infer<typeof LeavePeriodSchema>;
export type LeavePeriodRecord = z.infer<typeof LeavePeriodRecordSchema>;

/**
 *  USER SCHEMA (Client-facing)
 */
export const UserSchema = z.object({
    /** When the access was granted */
    accessGrantedAt: z.date().optional().describe("When the access was granted"),

    /** When the access was revoked */
    accessRevokedAt: z.date().optional().describe("When the access was revoked"),

    /** When the admin access was granted */
    adminAccessGrantedAt: z.date().optional().describe("When the admin access was granted"),

    /** When the member was archived (soft delete) */
    archivedAt: z.date().optional().describe("When the member was archived"),

    /** Member's birthday (month + day, no year) */
    birthday: BirthdaySchema.optional().describe("Member's birthday (month + day)"),

    /** Account creation timestamp (set by Auth Service) */
    created_time: z.date().describe("When the account was created"),

    /** Dates the user has been absent from an event */
    datesOfAbsence: z.array(z.date()).optional().describe("Dates the user has been absent from an event"),

    /** Dates the user has cancelled bookings for (cancelled more than 24 hours before session) */
    datesOfEarlyCancellation: z
        .array(z.date())
        .optional()
        .describe("Dates the user has cancelled bookings for (cancelled more than 24 hours before session)"),

    /** Dates the user has cancelled bookings late (within 24 hours of session start) */
    datesOfLateCancellation: z
        .array(z.date())
        .optional()
        .describe("Dates the user has cancelled bookings late (within 24 hours of session start)"),

    /** Display name shown to other users (from name step) */
    displayName: z.string().optional().describe("Public display name"),

    /** User's email address */
    email: z.string().email().optional().describe("User's email address"),

    /** Whether user is flagged for removal due to behavior */
    flaggedForRemovalAt: z.date().optional().describe("Flagged for removal"),

    /** When the user last attended an event */
    lastAttendedAt: z.date().optional().describe("When the user last attended an event"),

    /** Completed leave periods */
    leaveHistory: z.array(LeavePeriodSchema).optional().describe("Past leave periods"),

    /** Active leave reason (while on leave) */
    leaveReason: z.string().max(USER_LEAVE_TEXT_MAX).optional().describe("Reason for current leave"),

    /** When current leave started (if any) */
    leaveStartedAt: z.date().optional().describe("Start of current leave"),

    password: z.string().optional().describe("User's password"),

    /** User's phone number */
    phoneNumber: z
        .string()
        .refine((val) => isPossiblePhoneNumber(val), {
            message: "Invalid phone number",
        })
        .describe("User's phone number"),

    /** Total number of sessions attended */
    totalSessionsAttended: z.number().int().min(0).default(0).describe("Total sessions attended"),

    /** Unique user identifier (set by Auth Service) */
    uid: z.string().describe("Unique user identifier"),
});

/*
 * USER RECORD SCHEMA (Database/Server-facing)
 * Firestore Timestamps for dates; leaveHistory items use Timestamp (nested dates are not handled by toFirestoreSchema).
 */
export const UserRecordSchema = toFirestoreSchema(UserSchema.omit({ leaveHistory: true })).extend({
    leaveHistory: z.array(LeavePeriodRecordSchema).optional(),
});

// Type exports
export type User = z.infer<typeof UserSchema>;
export type UserRecord = z.infer<typeof UserRecordSchema>;
